import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  InputBase,
  Avatar,
  Drawer,
  FormLabel,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  tableCellClasses,
  Paper,
  Pagination,
  Tooltip,
  IconButton,
  Modal,
  Chip,
} from "@mui/material";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import AddIcon from "@mui/icons-material/Add";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CloseIcon from "@mui/icons-material/Close";
import { RiDeleteBinLine, RiEditLine } from "react-icons/ri";
import { GrView } from "react-icons/gr";
import { useLocation, useNavigate } from "react-router";
import moment from "moment";
import Controls from "../../Component/controls/Controls";
import { useForm } from "../../Utils/useForm";
import Notification from "../../Utils/Notification";
import { sellerStatusChange, getSellerList, deleteSeller } from "../../state/action/userAction";

const initialValues = { userName: "", email: "", mobile: "", status: "" };

const sellerStatusOptions = [
  { id: "1", title: "Active" },
  { id: "0", title: "Inactive" },
  { id: "3", title: "Delete" },
];

// ── Styled components ──────────────────────────────────────────────
const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#f8fafc",
    color: "#374151",
    fontSize: 13,
    fontWeight: 600,
    border: 0,
    padding: "14px 20px",
    whiteSpace: "nowrap",
    cursor: "pointer",
    userSelect: "none",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: "#111827",
    border: 0,
    padding: "13px 20px",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  backgroundColor: "#fff",
  "&:nth-of-type(odd)": { backgroundColor: "#fafafa" },
  "&:hover": { backgroundColor: "#f1f5f9 !important", cursor: "pointer" },
  "&:last-child td": { border: 0 },
}));

const FilterTextField = styled(TextField)({
  "& .MuiOutlinedInput-input": { padding: "10px 14px", color: "#000" },
  "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(107,114,128,0.4)",
    borderRadius: "8px",
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#000" },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#4f46e5" },
  "& .MuiInputLabel-root": { color: "rgba(92,91,91,0.7)", fontSize: "14px" },
  "& label.Mui-focused": { color: "#4f46e5" },
});

const FilterLabel = styled(FormLabel)({
  display: "block",
  fontWeight: 600,
  fontSize: "13px",
  color: "#374151",
  marginTop: "16px",
  marginBottom: "6px",
});

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 420,
  bgcolor: "#fff",
  borderRadius: "14px",
  boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
  p: 4,
  outline: 0,
};

const SortIcon = ({ flag }) =>
  flag ? (
    <ArrowUpwardIcon sx={{ fontSize: "13px", ml: 0.5, color: "rgba(107,114,128,0.6)", verticalAlign: "middle" }} />
  ) : (
    <ArrowDownwardIcon sx={{ fontSize: "13px", ml: 0.5, color: "rgba(107,114,128,0.6)", verticalAlign: "middle" }} />
  );

// ── Component ──────────────────────────────────────────────────────
export default function SellerList() {
  const [sellerLists, setSellerLists] = useState([]);
  const [pagePerSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [notify, setNotify] = useState({});
  const [openSwitchModel, setOpenSwitchModel] = useState(false);
  const [openDeleteModel, setOpenDeleteModel] = useState(false);
  const [status, setStatus] = useState("");
  const [switchId, setSwitchId] = useState(null);
  const [checked, setChecked] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [search, setSearch] = useState("");
  const [fieldName, setFieldName] = useState("");
  const [flagName, setFlagName] = useState(false);
  const [flagEmail, setFlagEmail] = useState(false);
  const [flagMobile, setFlagMobile] = useState(false);
  const [flagCompany, setFlagCompany] = useState(false);
  const [filterData, setFilterData] = useState(true);
  const [filter, setFilter] = useState(false);

  const { state } = useLocation();
  const navigate = useNavigate();
  const { values, handleInputChange, resetForm } = useForm(initialValues);

  const fetchSellerList = async (pg, name = "", flag) => {
    const string = Object.entries(values).map(([k, v]) => `&${k}=${v}`).join("");
    const query = `?page=${pg}&pagePerSize=${pagePerSize}&search=${search}${string}&sortBy=${name}&sortFlag=${flag}`;
    const res = await getSellerList(query);
    if (res.status === true) {
      setSellerLists(res.data);
      setTotalRecords(res.totalUser);
      setTotalPages(Math.ceil(res.totalUser / pagePerSize));
      setFilter(false);
    } else {
      setSellerLists([]);
      setTotalRecords(0);
      setTotalPages(0);
    }
  };

  useEffect(() => { fetchSellerList(page, "", ""); }, [search, filterData]);

  useEffect(() => {
    if (state?.notify === true) setNotify({ isOpen: true, message: "Seller Added Successfully.", type: "success" });
    if (state?.notify === "true1") setNotify({ isOpen: true, message: "Seller Updated Successfully.", type: "success" });
  }, [state]);

  const handleChangePage = (e, val) => {
    setPage(val);
    if (fieldName === "userName") fetchSellerList(val, fieldName, flagName);
    else if (fieldName === "email") fetchSellerList(val, fieldName, flagEmail);
    else if (fieldName === "mobile") fetchSellerList(val, fieldName, flagMobile);
    else if (fieldName === "company") fetchSellerList(val, fieldName, flagCompany);
    else fetchSellerList(val, "", "");
  };

  const dataSorting = (name) => {
    setFieldName(name);
    if (name === "userName") { setFlagName(!flagName); fetchSellerList(page, name, !flagName); }
    if (name === "email") { setFlagEmail(!flagEmail); fetchSellerList(page, name, !flagEmail); }
    if (name === "mobile") { setFlagMobile(!flagMobile); fetchSellerList(page, name, !flagMobile); }
    if (name === "company") { setFlagCompany(!flagCompany); fetchSellerList(page, name, !flagCompany); }
  };

  const handleChangeStatus = async () => {
    const res = await sellerStatusChange(switchId, { status: checked ? 1 : 0 });
    if (res?.status === true) {
      fetchSellerList(page, fieldName, flagName);
      setOpenSwitchModel(false);
      setNotify({ isOpen: true, message: res.message || "Status changed successfully.", type: "success" });
    } else {
      setNotify({ isOpen: true, message: res.message || "Something went wrong.", type: "error" });
    }
  };

  const handleDelete = async (id) => {
    const res = await deleteSeller(id);
    if (res?.status === true) {
      fetchSellerList(page, fieldName, flagName);
      setOpenDeleteModel(false);
      setNotify({ isOpen: true, message: res.message || "Deleted successfully.", type: "success" });
    } else {
      setNotify({ isOpen: true, message: res.message || "Something went wrong.", type: "error" });
    }
  };

  return (
    <>
      {/* ── Page Header ── */}
      <Box sx={{ px: 3, pt: 4, pb: 2, backgroundColor: "rgb(241,245,249)" }}>
        <Typography sx={{ fontWeight: 700, color: "#111827", fontSize: "22px" }}>
          Sellers
        </Typography>
        <Typography sx={{ color: "rgb(107,114,128)", fontSize: "13px", mt: 0.5 }}>
          Manage all registered sellers
        </Typography>
      </Box>

      {/* ── Toolbar ── */}
      <Box sx={{ px: 3, pb: 2, backgroundColor: "rgb(241,245,249)" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#fff",
              border: "1px solid rgba(0,0,0,0.1)",
              borderRadius: "10px",
              px: 1.5,
              py: 0.5,
              minWidth: 260,
            }}
          >
            <SearchIcon sx={{ color: "rgba(107,114,128,0.6)", fontSize: "20px", mr: 1 }} />
            <InputBase
              placeholder="Search sellers..."
              sx={{ fontSize: "14px", color: "#000", flex: 1 }}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </Box>
          <Box display="flex" gap={1.5}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/sellerAdd")}
              sx={{
                backgroundColor: "rgb(79,70,229)",
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "14px",
                px: 2.5,
                boxShadow: "none",
                "&:hover": { backgroundColor: "rgb(67,56,202)", boxShadow: "none" },
              }}
            >
              Add Seller
            </Button>
            <Button
              variant="outlined"
              startIcon={<FilterAltIcon />}
              onClick={() => setFilter(true)}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "14px",
                px: 2.5,
                color: "rgb(30,41,59)",
                borderColor: "rgba(30,41,59,0.3)",
                "&:hover": { backgroundColor: "rgb(30,41,59)", color: "#fff", borderColor: "rgb(30,41,59)" },
              }}
            >
              Filter
            </Button>
          </Box>
        </Box>
      </Box>

      {/* ── Table Card ── */}
      <Box sx={{ px: 3, pb: 4, backgroundColor: "rgb(241,245,249)" }}>
        <Box
          sx={{
            backgroundColor: "#fff",
            borderRadius: "14px",
            border: "1px solid #e5e7eb",
            overflow: "hidden",
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          }}
        >
          <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableCell onClick={() => dataSorting("userName")}>
                    Name <SortIcon flag={flagName} />
                  </StyledTableCell>
                  <StyledTableCell onClick={() => dataSorting("company")}>
                    Company Name <SortIcon flag={flagCompany} />
                  </StyledTableCell>
                  <StyledTableCell onClick={() => dataSorting("email")}>
                    Email <SortIcon flag={flagEmail} />
                  </StyledTableCell>
                  <StyledTableCell onClick={() => dataSorting("mobile")}>
                    Mobile <SortIcon flag={flagMobile} />
                  </StyledTableCell>
                  <StyledTableCell>Joined</StyledTableCell>
                  <StyledTableCell>Status</StyledTableCell>
                  <StyledTableCell align="center">Actions</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sellerLists.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6, color: "#6b7280", fontSize: "15px", border: 0 }}>
                      No sellers found
                    </TableCell>
                  </TableRow>
                ) : (
                  sellerLists.map((row, index) => (
                    <StyledTableRow
                      key={index}
                      onClick={() => navigate("/sellerDetail", { state: { seller: row?.userId, companyId: row?.companyId } })}
                    >
                      {/* Name with Avatar */}
                      <StyledTableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Avatar
                            sx={{
                              width: 34,
                              height: 34,
                              fontSize: "13px",
                              fontWeight: 700,
                              backgroundColor: "rgba(79,70,229,0.12)",
                              color: "rgb(79,70,229)",
                            }}
                          >
                            {row.userName?.charAt(0)?.toUpperCase() || "S"}
                          </Avatar>
                          <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#111827" }}>
                            {row.userName}
                          </Typography>
                        </Box>
                      </StyledTableCell>

                      <StyledTableCell>{row.companyName || "—"}</StyledTableCell>
                      <StyledTableCell sx={{ color: "#6b7280" }}>{row.email}</StyledTableCell>
                      <StyledTableCell>{row.mobile}</StyledTableCell>

                      {/* Joined date + relative time */}
                      <StyledTableCell sx={{ color: "#6b7280", fontSize: "13px" }}>
                        {moment(row.createDate).format("DD MMM YYYY")}
                        <Typography sx={{ fontSize: "11px", color: "#9ca3af", mt: 0.2 }}>
                          {moment(row.createDate).fromNow()}
                        </Typography>
                      </StyledTableCell>

                      {/* Status chip */}
                      <StyledTableCell onClick={(e) => e.stopPropagation()}>
                        <Chip
                          label={row.sellerStatus == "1" ? "Active" : "Inactive"}
                          size="small"
                          onClick={() => {
                            setOpenSwitchModel(true);
                            setStatus(row.sellerStatus == "1" ? "Inactive" : "Active");
                            setSwitchId(row.userId);
                            setChecked(row.sellerStatus != "1");
                          }}
                          sx={{
                            fontWeight: 600,
                            fontSize: "12px",
                            cursor: "pointer",
                            backgroundColor: row.sellerStatus == "1" ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)",
                            color: row.sellerStatus == "1" ? "rgb(22,163,74)" : "rgb(220,38,38)",
                            border: `1px solid ${row.sellerStatus == "1" ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.3)"}`,
                          }}
                        />
                      </StyledTableCell>

                      {/* Actions */}
                      <StyledTableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <Box display="flex" justifyContent="center" gap={0.5}>
                          <Tooltip title="View">
                            <IconButton
                              size="small"
                              onClick={() => navigate("/sellerDetail", { state: { seller: row?.userId, companyId: row?.companyId } })}
                              sx={{ color: "#6b7280", "&:hover": { color: "rgb(79,70,229)", backgroundColor: "rgba(79,70,229,0.08)" } }}
                            >
                              <GrView size={15} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => navigate("/sellerEdit", { state: { seller: row?.userId, companyId: row?.companyId } })}
                              sx={{ color: "#6b7280", "&:hover": { color: "rgb(34,197,94)", backgroundColor: "rgba(34,197,94,0.08)" } }}
                            >
                              <RiEditLine size={15} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => { setDeleteId(row.userId); setOpenDeleteModel(true); }}
                              sx={{ color: "#6b7280", "&:hover": { color: "rgb(239,68,68)", backgroundColor: "rgba(239,68,68,0.08)" } }}
                            >
                              <RiDeleteBinLine size={15} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {sellerLists.length > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2.5,
                py: 2,
                borderTop: "1px solid #f3f4f6",
              }}
            >
              <Typography sx={{ fontSize: "13px", color: "#6b7280" }}>
                Showing {Math.min((page - 1) * pagePerSize + 1, totalRecords)}–{Math.min(page * pagePerSize, totalRecords)} of {totalRecords} results
              </Typography>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handleChangePage}
                shape="rounded"
                size="small"
                sx={{
                  "& .MuiButtonBase-root": { color: "#374151", fontSize: "13px" },
                  "& .Mui-selected": { backgroundColor: "rgb(79,70,229) !important", color: "#fff !important" },
                }}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* ── Status Change Modal ── */}
      <Modal open={openSwitchModel} onClose={() => setOpenSwitchModel(false)}>
        <Box sx={modalStyle}>
          <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#111827", mb: 1 }}>
            Change Status
          </Typography>
          <Typography sx={{ fontSize: "14px", color: "#6b7280", mb: 3 }}>
            Are you sure you want to mark this seller as <strong style={{ color: "#111827" }}>{status}</strong>?
          </Typography>
          <Box display="flex" justifyContent="flex-end" gap={1.5}>
            <Button
              onClick={() => setOpenSwitchModel(false)}
              variant="outlined"
              sx={{ textTransform: "none", borderRadius: "8px", color: "#374151", borderColor: "#d1d5db" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleChangeStatus}
              variant="contained"
              sx={{ textTransform: "none", borderRadius: "8px", backgroundColor: "rgb(79,70,229)", boxShadow: "none", "&:hover": { backgroundColor: "rgb(67,56,202)", boxShadow: "none" } }}
            >
              Confirm
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* ── Delete Modal ── */}
      <Modal open={openDeleteModel} onClose={() => setOpenDeleteModel(false)}>
        <Box sx={modalStyle}>
          <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#111827", mb: 1 }}>
            Delete Seller
          </Typography>
          <Typography sx={{ fontSize: "14px", color: "#6b7280", mb: 3 }}>
            Are you sure you want to delete this seller? This action cannot be undone.
          </Typography>
          <Box display="flex" justifyContent="flex-end" gap={1.5}>
            <Button
              onClick={() => setOpenDeleteModel(false)}
              variant="outlined"
              sx={{ textTransform: "none", borderRadius: "8px", color: "#374151", borderColor: "#d1d5db" }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleDelete(deleteId)}
              variant="contained"
              sx={{ textTransform: "none", borderRadius: "8px", backgroundColor: "rgb(239,68,68)", boxShadow: "none", "&:hover": { backgroundColor: "rgb(220,38,38)", boxShadow: "none" } }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* ── Filter Drawer ── */}
      <Drawer anchor="right" open={filter} onClose={() => { setFilter(false); resetForm(); setFilterData(!filterData); }}>
        <Box
          sx={{ width: 320, height: "100%", display: "flex", flexDirection: "column", backgroundColor: "#fff" }}
          component="form"
          onSubmit={(e) => { e.preventDefault(); setFilterData(!filterData); setFilter(false); }}
        >
          <Box sx={{ px: 3, py: 2.5, borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontWeight: 700, fontSize: "18px", color: "#111827" }}>Filter</Typography>
            <IconButton size="small" onClick={() => setFilter(false)} sx={{ color: "#6b7280" }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ px: 3, py: 2, flex: 1, overflowY: "auto" }}>
            <FilterLabel>Name</FilterLabel>
            <FilterTextField fullWidth name="userName" value={values.userName} onChange={handleInputChange} />

            <FilterLabel>Email</FilterLabel>
            <FilterTextField fullWidth name="email" value={values.email} onChange={handleInputChange} />

            <FilterLabel>Phone Number</FilterLabel>
            <FilterTextField fullWidth name="mobile" value={values.mobile} onChange={handleInputChange} />

            <FilterLabel>Status</FilterLabel>
            <Controls.SelectBox
              fullWidth
              name="status"
              value={values.status}
              onChange={handleInputChange}
              options={sellerStatusOptions}
            />
          </Box>

          <Box sx={{ px: 3, py: 2.5, borderTop: "1px solid #f3f4f6", display: "flex", gap: 1.5 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => { resetForm(); setFilterData(!filterData); setFilter(false); }}
              sx={{ textTransform: "none", borderRadius: "10px", color: "#374151", borderColor: "#d1d5db", fontWeight: 600 }}
            >
              Reset
            </Button>
            <Button
              fullWidth
              type="submit"
              variant="contained"
              sx={{ textTransform: "none", borderRadius: "10px", backgroundColor: "rgb(79,70,229)", boxShadow: "none", fontWeight: 600, "&:hover": { backgroundColor: "rgb(67,56,202)", boxShadow: "none" } }}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Drawer>

      <Notification notify={notify} setNotify={setNotify} />
    </>
  );
}
